import { roleLabel } from './auth';
import { ACTIVITIES_VISIBLE, type ActivityType } from './constants';

/**
 * Prototype `activityTypeLabel` (line 1816). The prototype had no label for task/delivery/attendance/auth
 * (they fell back to "Sistem"), but task.md §4.12 lists all ten types as activity-log types, so each one
 * gets a label and a filter option; the four added labels are new strings (KNOWN_LIMITATIONS L30).
 */
export const ACTIVITY_TYPE_LABELS: Readonly<Record<string, string>> = {
  sale: 'Shitje',
  cart: 'Shitje',
  purchase: 'Blerje',
  buy: 'Blerje',
  stock: 'Stok / sistem',
  debt: 'Borxh',
  payment: 'Pagesë',
  request: 'Kërkesë klienti',
  task: 'Detyrë',
  delivery: 'Dorëzim',
  attendance: 'Pjesëmarrje',
  auth: 'Hyrje/Dalje',
};

export const ACTIVITY_FALLBACK_LABEL = 'Sistem';

/** Default `user_name` when no user is signed in (prototype `log()`, line 1608). */
export const ACTIVITY_SYSTEM_USER = 'Sistem';

export function activityTypeLabel(type: string | null | undefined): string {
  return ACTIVITY_TYPE_LABELS[type ?? ''] ?? ACTIVITY_FALLBACK_LABEL;
}

export type ActivityFilter = 'all' | ActivityType;

/** Options of `#systemActivityType` (prototype line 1378) plus the Phase-1 'auth' type. */
export const ACTIVITY_TYPE_OPTIONS: readonly { value: ActivityFilter; label: string }[] = [
  { value: 'all', label: 'Të gjitha' },
  { value: 'sale', label: 'Shitje' },
  { value: 'purchase', label: 'Blerje' },
  { value: 'stock', label: 'Stok / tjera' },
  { value: 'debt', label: 'Borxhe' },
  { value: 'payment', label: 'Pagesa' },
  { value: 'request', label: 'Kërkesa klientësh' },
  { value: 'task', label: 'Detyrat' },
  { value: 'delivery', label: 'Dorëzimet' },
  { value: 'attendance', label: 'Pjesëmarrja' },
  { value: 'auth', label: 'Hyrje/Dalje' },
];

/** Prototype filter (line 1817): 'sale' also matches legacy 'cart', 'purchase' matches legacy 'buy'. */
export function activityMatchesFilter(type: string, filter: string): boolean {
  return (
    filter === 'all' ||
    type === filter ||
    (filter === 'sale' && type === 'cart') ||
    (filter === 'purchase' && type === 'buy')
  );
}

/** Newest first, capped at ACTIVITIES_VISIBLE (prototype keeps the last 1,000; the DB keeps all). */
export function visibleActivities<T extends { created_at: string }>(list: readonly T[]): T[] {
  return [...list]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0))
    .slice(0, ACTIVITIES_VISIBLE);
}

/** Second line of an activity row (line 1817): `<label> · <detail> · nga <user>`. */
export function activitySubline(a: {
  type: string;
  detail?: string | null;
  user_name?: string | null;
}): string {
  return (
    activityTypeLabel(a.type) +
    (a.detail ? ' · ' + a.detail : '') +
    (a.user_name ? ' · nga ' + a.user_name : '')
  );
}

/** Auth activity texts (prototype lines 3102, 3105, 3127), logged under type 'auth'. */
export const AUTH_ACTIVITY = {
  login: 'Hyrje në ERP',
  logout: 'Dalje nga ERP',
  userSaved: 'U përditësua hyrja',
} as const;

/** Detail for login / user-saved entries: `<name> · <Drejtor|Staf>`. */
export function authActivityDetail(name: string, role: string | null | undefined): string {
  return name + ' · ' + roleLabel(role);
}
